import Image from 'next/image';
import cn from 'classnames';
import { memo, useEffect } from 'react';
import {
  CategoryCarouselType,
  CategoryCarousel,
} from '@adapters/prismic/types/plp';
import { useSwiper } from 'swiper/react';
import TagManager from 'react-gtm-module';

import styles from './CarouselSlide.module.scss';

interface CarouselSlideProps {
  slide: CategoryCarousel;
  carouselType: CategoryCarouselType;
  swiperSize?: number | null;
  headingWithoutSpaces?: string;
  index: number;
}

const CarouselSlide = memo(
  ({
    slide,
    carouselType,
    swiperSize,
    headingWithoutSpaces,
    index,
  }: CarouselSlideProps) => {
    const swiper = useSwiper();
    const imageUrl = slide?.image?.url ?? '';

    useEffect(() => {
      if (
        carouselType === CategoryCarouselType.featured &&
        swiperSize &&
        swiperSize > 808
      ) {
        const slideElement = document.querySelector(
          `.slide-${headingWithoutSpaces}`
        );

        const handleMouseEnter = () => {
          const slideRect = slideElement?.getBoundingClientRect();
          const carouselRect = swiper.el.getBoundingClientRect();

          if (
            (slideRect && slideRect.right === carouselRect.right) ||
            (slideRect && slideRect.right === carouselRect.right - 20)
          ) {
            swiper.slideNext(300);
          }
        };

        slideElement?.addEventListener('mouseenter', handleMouseEnter);

        return () => {
          slideElement?.removeEventListener('mouseenter', handleMouseEnter);
        };
      }
    }, [carouselType, headingWithoutSpaces, slide, swiper, swiperSize]);

    const trackClick = () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'user_interaction',
          event_name: 'select_carousel_slide',
          interaction: {
            carousel_slide_name: slide?.label,
            carousel_type: carouselType,
            index: index,
          },
        },
      });
    };

    return (
      <a
        href={slide.url}
        className={`slide-${headingWithoutSpaces} ${styles.link}`}
        onClick={trackClick}
      >
        <div
          className={cn(styles.image, {
            [styles.otherType]: carouselType === CategoryCarouselType.other,
            [styles.noImage]: !imageUrl,
            [styles.brandsType]: carouselType === CategoryCarouselType.brands,
          })}
        >
          {slide?.image?.url ? (
            <Image
              src={imageUrl}
              alt={slide.label}
              fill
              sizes="100vw"
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          ) : (
            <span className={styles.slideText}>{slide.label}</span>
          )}
        </div>
        {imageUrl && <p className={styles.labelText}>{slide.label}</p>}
      </a>
    );
  }
);

export default CarouselSlide;
